<template>
    <app-layout title="Picking">
        <page-header>
            <template #title>Picking</template>
            <template #action></template>
        </page-header>

        <div class="px-4 pb-12">
            <div class="flex flex-col lg:flex-row gap-8">
                <!-- Left Column - Picking Form -->
                <div class="w-full lg:w-1/3 flex flex-col gap-y-2">
                    <div>
                        <div class="text-2xl font-bold">
                            {{ picker?.name || 'No Picker Selected' }}
                        </div>
                        <jet-button class="mr-2" @click="showPickerCodeModal = true">
                            Update Picker
                        </jet-button>
                    </div>

                    <div>
                        <jet-label value="Division"/>
                        <multiselect 
                            v-model="orderType"
                            :options="[
                                { value: 'retail', label: 'Retail' },
                                { value: 'wholesale', label: 'Wholesale' },
                                { value: 'school', label: 'Schools' }
                            ]"
                        >
                        </multiselect>
                    </div>

                    <div>
                        <jet-label value="Date:" />
                        <jet-input 
                            v-model="orderDate" 
                            type="date" 
                            class="w-full"
                        />
                    </div>
                    
                    <jet-button
                        class="px-8 py-4 font-bold text-xl text-center" 
                        @click.stop="pickNextOrder"
                        :disabled="!picker || !orderType || !orderDate"
                    >
                        Pick Next Order
                    </jet-button>

                    <div v-if="error" class="text-red-500">
                        {{ error }}
                    </div>
                </div>

                <!-- Right Column - Picking Statistics -->
                <div v-if="statsEnabled && orderDate" class="w-full lg:w-2/3">
                    <div class="flex justify-between items-center mb-2">
                        <h2 class="text-xl font-bold">Picking Statistics</h2>
                        <jet-secondary-button @click="togglePickingStats">
                            {{ pickingStatsExpanded ? 'Collapse' : 'Expand' }}
                        </jet-secondary-button>
                    </div>
                    <div class="rounded overflow-hidden border border-b-0 overflow-y-auto"
                         :class="pickingStatsExpanded ? '' : 'max-h-60'">
                        <table class="table w-full">
                            <thead class="sticky top-0 bg-white z-10">
                                <tr>
                                    <th class="text-left">Picker</th>
                                    <th class="text-right">
                                        <BetterTooltip>
                                            <template #reference>
                                                Items Picked*
                                            </template>
                                            <template #floating>
                                                Total quantity of items picked
                                            </template>
                                        </BetterTooltip>
                                    </th>
                                    <th class="text-right">Hours</th>
                                    <th class="text-right">
                                        <BetterTooltip>
                                            <template #reference>
                                                Hours Away*
                                            </template>
                                            <template #floating>
                                                Breaks include: gaps of 15+ minutes, activity changes (picking ↔ checking), and checking periods
                                            </template>
                                        </BetterTooltip>
                                    </th>
                                    <th class="text-right">
                                        
                                        <BetterTooltip>
                                            <template #reference>
                                                Picking Hours*
                                            </template>
                                            <template #floating>
                                                Hours - Hours Away
                                            </template>
                                        </BetterTooltip>
                                    </th>
                                    <th class="text-right">
                                        <BetterTooltip>
                                            <template #reference>
                                                Items/Hour*
                                            </template>
                                            <template #floating>
                                                Total items picked divided by total hours worked (excludes breaks)
                                            </template>
                                        </BetterTooltip>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loadingPickingStats">
                                    <td colspan="6" class="text-center py-4">
                                        <div class="flex items-center justify-center">
                                            <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                                        </div>
                                    </td>
                                </tr>
                                <tr v-else-if="pickingStats.length == 0">
                                    <td colspan="6" class="text-center">- No Data Found -</td>
                                </tr>
                                <tr v-for="stat in sortedPickingStats" :key="stat.picker_id"
                                    :class="picker && stat.picker_id === picker.id ? 'currentPicker' : ''">
                                    <td class="text-left">{{ stat.picker_name }}</td>
                                    <td class="text-right">{{ stat.items_picked }}</td>
                                    <td class="text-right">
                                        <BetterTooltip>
                                            <template #reference>
                                                {{ stat.hours_total }}*
                                            </template>
                                            <template #floating>
                                                <div class="text-sm max-h-80 overflow-y-auto">
                                                    <div v-if="stat.periods && stat.periods.length > 0">
                                                        <div class="font-semibold mb-1">Timeline:</div>
                                                        <div class="flex flex-col gap-y-1">
                                                            <div v-for="(p, i) in stat.periods" :key="i" class="flex items-center gap-x-2">
                                                                <Pill :color="p.type === 'picking' ? 'primary-green' : p.type === 'checking' ? 'primary-red' : p.type === 'normal_break' ? 'blue' : 'primary-red'" :size="'sm'" :variant="'soft'">{{ p.type === 'picking' ? 'Picking' : p.type === 'checking' ? 'Checking' : p.type === 'normal_break' ? 'Break' : 'Switch' }}</Pill>
                                                                
                                                                {{ formatTimestamp(p.start, 'h:mm a') }} - {{ formatTimestamp(p.end, 'h:mm a') }} ({{ p.duration_hours }} hrs)
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-else>
                                                        No activity recorded
                                                    </div>
                                                </div>
                                            </template>
                                        </BetterTooltip>
                                    </td>
                                    <td class="text-right">
                                        {{ stat.time_away_hours }}
                                    </td>
                                    <td class="text-right">{{ stat.picking_time_hours }}</td>
                                    <td class="text-right">{{ stat.items_per_hour }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="mt-6">
                        <div class="flex justify-between items-center mb-2">
                            <h2 class="text-xl font-bold">Checking Statistics</h2>
                            <jet-secondary-button @click="toggleCheckingStats">
                                {{ checkingStatsExpanded ? 'Collapse' : 'Expand' }}
                            </jet-secondary-button>
                        </div>
                        <div class="rounded overflow-hidden border border-b-0 overflow-y-auto"
                             :class="checkingStatsExpanded ? '' : 'max-h-60'">
                            <table class="table w-full">
                                <thead class="sticky top-0 bg-white z-10">
                                    <tr>
                                        <th class="text-left">Checker</th>
                                        <th class="text-right">
                                            <BetterTooltip>
                                                <template #reference>
                                                    Items Checked*
                                                </template>
                                                <template #floating>
                                                    Total quantity of items checked
                                                </template>
                                            </BetterTooltip>
                                        </th>
                                        <th class="text-right">Hours</th>
                                        <th class="text-right">
                                            <BetterTooltip>
                                                <template #reference>
                                                    Hours Away*
                                                </template>
                                                <template #floating>
                                                    Breaks include: gaps of 15+ minutes, activity changes (picking ↔ checking), and picking periods
                                                </template>
                                            </BetterTooltip>
                                        </th>
                                        <th class="text-right">
                                            <BetterTooltip>
                                                <template #reference>
                                                    Checking Hours*
                                                </template>
                                                <template #floating>
                                                    Hours - Hours Away
                                                </template>
                                            </BetterTooltip>
                                        </th>
                                        <th class="text-right">
                                            <BetterTooltip>
                                                <template #reference>
                                                    Items/Hour*
                                                </template>
                                                <template #floating>
                                                    Total items checked divided by total hours worked (excludes breaks)
                                                </template>
                                            </BetterTooltip>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="loadingCheckingStats">
                                        <td colspan="6" class="text-center py-4">
                                            <div class="flex items-center justify-center">
                                                <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-else-if="checkingStats.length == 0">
                                        <td colspan="6" class="text-center">- No Data Found -</td>
                                    </tr>
                                    <tr v-for="stat in sortedCheckingStats" :key="stat.checker_id"
                                        :class="checker && stat.checker_id === checker.id ? 'currentChecker' : ''">
                                        <td class="text-left">{{ stat.checker_name }}</td>
                                        <td class="text-right">{{ stat.items_checked }}</td>
                                        <td class="text-right">
                                            <BetterTooltip>
                                                <template #reference>
                                                    {{ stat.hours_total }}*
                                                </template>
                                                <template #floating>
                                                    <div class="text-sm max-h-80 overflow-y-auto">
                                                        <div v-if="stat.periods && stat.periods.length > 0">
                                                            <div class="font-semibold mb-1">Timeline:</div>
                                                            <div class="flex flex-col gap-y-1">
                                                                <div v-for="(p, i) in stat.periods" :key="i" class="flex items-center gap-x-2">
                                                                    <Pill :color="p.type === 'picking' ? 'primary-green' : p.type === 'checking' ? 'primary-red' : p.type === 'normal_break' ? 'blue' : 'primary-red'" :size="'sm'" :variant="'soft'">{{ p.type === 'picking' ? 'Picking' : p.type === 'checking' ? 'Checking' : p.type === 'normal_break' ? 'Break' : 'Switch' }}</Pill>
                                                                    {{ formatTimestamp(p.start, 'h:mm a') }} - {{ formatTimestamp(p.end, 'h:mm a') }} ({{ p.duration_hours }} hrs)
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div v-else>
                                                            No activity recorded
                                                        </div>
                                                    </div>
                                                </template>
                                            </BetterTooltip>
                                        </td>
                                        <td class="text-right">
                                            {{ stat.time_away_hours }}
                                        </td>
                                        <td class="text-right">{{ stat.checking_time_hours }}</td>
                                        <td class="text-right">{{ stat.items_per_hour }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
                
            <div
                v-if="picker && orderType && orderDate"
                class="flex flex-col lg:flex-row gap-8 my-12"
            >
                <!-- Left Column - Order Queue -->
                <div class="w-full lg:w-1/2">
                    <h2 class="text-xl font-bold mb-2">Order Queue</h2>
                    <div class="rounded overflow-hidden border border-b-0 max-h-96 overflow-y-auto">
                        <table class="table w-full">
                            <thead class="sticky top-0 bg-white z-10">
                                <tr>
                                    <th class="text-left">Status</th>
                                    <th class="text-left">Load</th>
                                    <th class="text-left">Order #</th>
                                    <th class="text-left">Customer</th>
                                    <th class="text-right">Items</th>
                                    <th class=""></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loadingOrderQueue">
                                    <td colspan="8" class="text-center py-4">
                                        <div class="flex items-center justify-center">
                                            <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                                        </div>
                                    </td>
                                </tr>
                                <tr v-else-if="orderQueue.length == 0">
                                    <td colspan="8" class="text-center">- No Orders in Queue -</td>
                                </tr>
                                <tr v-for="order in orderQueue" :key="order.id" 
                                    :class="{
                                        'bg-blue-50': order.is_current_picker,
                                        'bg-yellow-50': order.is_locked && !order.is_current_picker,
                                        'bg-green-50': !order.is_locked
                                    }">
                                    <td class="text-left">
                                        <Pill :color="order.is_current_picker ? 'blue' : order.is_locked ? 'primary-red' : 'primary-green'" :size="'sm'" :variant="'soft'">{{ order.is_current_picker ? 'Yours' : order.is_locked ? `${order.locked_by}'s` : 'Available' }}</Pill>
                                    </td>
                                    <td class="text-left whitespace-nowrap">
                                        <div>
                                            <Link v-if="order.load_id" :href="route('loads.show', order.load_id)" class="hover:underline">
                                                {{ order.load_title }}
                                            </Link>
                                            <span v-else>{{ order.load_title }}</span>
                                        </div>
                                        <div class="text-xs text-gray-500">
                                            {{ formatTimestamp(order.departure_time, 'h:mm a') }}
                                        </div>
                                    </td>
                                    <td class="text-left whitespace-nowrap">{{ order.order_number }}</td>
                                    <td class="text-left">{{ order.customer }}</td>
                                    <td class="text-right">{{ order.total_items }}</td>
                                    <td class="text-center">
                                        <jet-secondary-button v-if="!order.is_locked" type="button" class="leading-none" @click="pickOrder(order)">
                                            Pick
                                        </jet-secondary-button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Right Column - Orders Picked by Picker -->
                <div class="w-full lg:w-1/2">
                    <h2 class="text-xl font-bold mb-2">Your Picked Orders</h2>
                    <div class="rounded overflow-hidden border border-b-0 max-h-96 overflow-y-auto">
                        <table class="table w-full">
                            <thead class="sticky top-0 bg-white z-10">
                                <tr>
                                    <th class="text-left">Order #</th>
                                    <th class="text-left">Customer</th>
                                    <th class="text-right">Items</th>
                                    <th class="text-left">Packaging</th>
                                    <th class="text-right">Picked</th>
                                    <th class="text-center"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loadingPickedOrders">
                                    <td colspan="7" class="text-center py-4">
                                        <div class="flex items-center justify-center">
                                            <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                                        </div>
                                    </td>
                                </tr>
                                <tr v-else-if="ordersPickedByPicker.length == 0">
                                    <td colspan="7" class="text-center">- No Data Found -</td>
                                </tr>
                                <tr v-for="order in ordersPickedByPicker" :key="order.id">
                                    <td class="text-left">{{ order.order_number }}</td>
                                    <td class="text-left">{{ order.customer }}</td>
                                    <td class="text-right">{{ order.total_items }}</td>
                                    <td class="text-left whitespace-nowrap">
                                        <div class="flex gap-x-2 no-wrap">
                                            <span v-if="order.box_count > 0"><font-awesome-icon :icon="['fas', 'box']" class="mr-1" /> {{ order.box_count }}</span>
                                            <span v-if="order.pallet_count > 0"><font-awesome-icon :icon="['fas', 'pallet']" class="mr-1" /> {{ order.pallet_count }}</span>
                                            <span v-if="order.tote_count > 0"><font-awesome-icon :icon="['fas', 'snowflake']" class="mr-1" /> {{ order.tote_count }}</span>
                                        </div>
                                    </td>
                                    <td class="text-right">{{ formatTimestamp(order.picked_at, 'h:mm a') }}</td>
                                    <td class="text-center">
                                        <jet-secondary-button v-if="order.box_count > 0" type="button" class="leading-none" @click="printLabels(order)">
                                            <img src="/images/icons/print_w.svg" width="16" height="16" style="min-width: 16px;" alt="Print Labels">
                                        </jet-secondary-button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>

    <!-- Picker Login -->
    <jet-dialog-modal :show="showPickerCodeModal" @close="cancelPickerCode">
        <template #title>
                <h3 class="text-2xl">Update Picker</h3>
        </template>

        <template #content>
            <form @submit.prevent="updatePicker">
                <div class="mb-2">
                    <jet-label value="Code:" class="mb-0" />
                    <jet-input v-model="pickerCode" ref="pickerCode" type="text" class="w-full" required />
                    <jet-input-error :message="pickerCodeError" class="m1" />
                </div>

                <input type="submit" class="hidden" ref="submitPickerCode" />
            </form>
        </template>

        <template #footer>
            <jet-button class="mr-2" @click="updatePicker">
                Update
            </jet-button>

            <jet-tertiary-button @click="cancelPickerCode">
                Cancel
            </jet-tertiary-button>
        </template>
    </jet-dialog-modal>
</template>

<script>
import { usePage, useForm, Link } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue'
import JetDialogModal from '@/Jetstream/DialogModal.vue'
import JetDropdown from '@/Jetstream/Dropdown';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import BetterTooltip from '@/Jetstream/BetterTooltip';
import Pill from '@/Jetstream/Pill';

import '@vueform/multiselect/themes/default.css';

export default {
    props: ['origPicker', 'origChecker', 'initOrderType'],

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetConfirmationModal,
        JetDialogModal,
        JetDropdown,
        JetInput,
        JetInputError,
        JetLabel,
        Link,
        Multiselect,
        JetSecondaryButton,
        BetterTooltip,
        Pill
    },

    data(){
        return{
            interval: null,

            picker: this.origPicker,
            checker: this.origChecker,
            showPickerCodeModal: false,
            pickerCode: null,
            pickerCodeError: null,
            orderType: this.initOrderType === 'schools' ? 'school' : this.initOrderType,
            orderDate: new Date().toISOString().split('T')[0],
            error: null,
            ordersPickedByPicker: [],
            orderQueue: [],
            pickingStats: [],
            checkingStats: [],
            loadingPickingStats: false,
            loadingCheckingStats: false,
            loadingOrderQueue: false,
            loadingPickedOrders: false,
            pickingStatsExpanded: false,
            checkingStatsExpanded: false,
            statsEnabled: true,
        }
    },
    

    created () {
        this.interval = setInterval(() => {
            this.fetchOrderQueue();
            if (this.statsEnabled) {
                this.fetchPickingStats();
                this.fetchCheckingStats();
            }
        }, 60 * 1000)
    },

    beforeUnmount() {
        clearInterval(this.interval);
    },

    mounted() {
        this.fetchOrdersPickedByPicker();
        this.fetchOrderQueue();
        if (this.statsEnabled) {
            this.fetchPickingStats();
            this.fetchCheckingStats();
        }
    },

    computed: {
        sortedPickingStats() {
            if (!this.pickingStats.length) return [];
            
            // If there's a current picker, put them first, then sort the rest alphabetically
            if (this.picker) {
                const currentPickerStats = this.pickingStats.find(stat => stat.picker_id === this.picker.id);
                const otherStats = this.pickingStats.filter(stat => stat.picker_id !== this.picker.id);
                
                // Sort other stats alphabetically
                otherStats.sort((a, b) => a.picker_name.localeCompare(b.picker_name));
                
                // Return current picker first, then others
                return currentPickerStats ? [currentPickerStats, ...otherStats] : otherStats;
            }
            
            // If no current picker, sort all alphabetically
            return [...this.pickingStats].sort((a, b) => a.picker_name.localeCompare(b.picker_name));
        },
        sortedCheckingStats() {
            if (!this.checkingStats.length) return [];

            if (this.checker) {
                const currentCheckerStats = this.checkingStats.find(stat => stat.checker_id === this.checker.id);
                const otherStats = this.checkingStats.filter(stat => stat.checker_id !== this.checker.id);
                otherStats.sort((a, b) => a.checker_name.localeCompare(b.checker_name));
                return currentCheckerStats ? [currentCheckerStats, ...otherStats] : otherStats;
            }

            return [...this.checkingStats].sort((a, b) => a.checker_name.localeCompare(b.checker_name));
        }
    },

    methods:{
        updatePickerIfNeeded (response = null, callback = null) {
            if (response === null || response === 'invalid_picker_session')
            {
                this.picker = null;
                this.showPickerCodeModal = true;
            }
            else if (typeof callback == 'function') callback();
        },

        cancelPickerCode() {
            this.showPickerCodeModal = false;
            this.pickerCode = null;
        },

        updatePicker() {
            this.pickerCodeError = null;
            if (this.pickerCodeErrorTimeout) clearTimeout(this.pickerCodeErrorTimeout);

            axios.post(route('orders.update_picker', { picker_code: this.pickerCode }))
            .then((response) => {
                if (response.data == 'invalid_picker_code')
                {
                    this.pickerCodeError = 'Invalid Picker Code';
                    this.pickerCodeErrorTimeout = setTimeout(() => {
                        this.pickerCodeError = null;
                    }, 4 * 1000);
                }
                else
                {
                    this.picker = response.data;
                    this.cancelPickerCode();
                }
            });
        },

        updateOrderType () {
            axios.post(route('orders.update_order_type', {order_type: this.orderType}));
        },

        pickOrder(order) {
            const query = `orders[]=${order.id}`;
            this.$inertia.visit(`/multiPicking?${query}`);
        },

        pickNextOrder(){
            axios.get(`/orderForPicking`,{
                params: {
                    orderType: this.orderType,
                    orderDate: this.orderDate,
                }
            })
            .then((response) => {
                this.updatePickerIfNeeded(response.data, () => {
                    const query = response.data.orders.map(id => `orders[]=${id}`).join('&');
                    this.$inertia.visit(`/multiPicking?${query}`);
                });
            })
            .catch((error)=>{
                this.error = `${error.response?.data?.message || error.message}`;

                setTimeout(() => {
                    this.error = null;
                }, 6 * 1000)
            })
        },

        fetchOrdersPickedByPicker() {
            this.ordersPickedByPicker = [];

            this.loadingPickedOrders = true;
            axios.get(route('picking.orders_picked_by_picker'), {
                params: {
                    order_type: this.orderType,
                    order_date: this.orderDate
                }
            })
                .then(res => {
                    this.ordersPickedByPicker = res.data;
                })
                .finally(() => {
                    this.loadingPickedOrders = false;
                });
        },

        fetchOrderQueue() {
            this.orderQueue = [];

            if (!this.orderType || !this.orderDate) {
                return;
            }
            this.loadingOrderQueue = true;
            axios.get('/orderQueue', {
                params: {
                    orderType: this.orderType,
                    orderDate: this.orderDate
                }
            })
                .then(res => {
                    this.orderQueue = res.data;
                })
                .catch(error => {
                    console.error('Error fetching order queue:', error);
                    this.orderQueue = [];
                })
                .finally(() => {
                    this.loadingOrderQueue = false;
                });
        },

        fetchPickingStats() {
            this.pickingStats = [];

            if (!this.statsEnabled || !this.orderDate) {
                return;
            }
            this.loadingPickingStats = true;
            const params = { order_date: this.orderDate };
            if (this.orderType) {
                params.order_type = this.orderType;
            }
            axios.get('/picking/stats', {
                params
            })
            .then(res => {
                this.pickingStats = res.data;
            })
            .catch(() => {
                this.pickingStats = [];
            })
            .finally(() => {
                this.loadingPickingStats = false;
            });
        },

        fetchCheckingStats() {
            this.checkingStats = [];

            if (!this.statsEnabled || !this.orderDate) {
                return;
            }
            this.loadingCheckingStats = true;
            const params = { order_date: this.orderDate };
            if (this.orderType) {
                params.order_type = this.orderType;
            }
            axios.get(route('picking.checking_stats'), {
                params
            })
            .then(res => {
                this.checkingStats = res.data;
            })
            .catch(() => {
                this.checkingStats = [];
            })
            .finally(() => {
                this.loadingCheckingStats = false;
            });
        },

        togglePickingStats() {
            this.pickingStatsExpanded = !this.pickingStatsExpanded;
        },

        toggleCheckingStats() {
            this.checkingStatsExpanded = !this.checkingStatsExpanded;
        },

        // Printing
        printLabels(order) {
            if (this.shouldAutoprint('labels')) {
                this.autoprintWithPrinterPrompt(route('orders.print'), { orders: order.id, printType: 'labels' });
            } else {
                window.open((route('orders.print', { orders: order.id, printType: 'labels'} )), "_blank");
            }
        },
    },

    watch: {
        orderType () {
            this.updateOrderType();
            this.fetchOrdersPickedByPicker();
            this.fetchOrderQueue();
            if (this.statsEnabled) {
                this.fetchPickingStats();
                this.fetchCheckingStats();
            }
        },

        orderDate () {
            this.fetchOrdersPickedByPicker();
            this.fetchOrderQueue();
            if (this.statsEnabled) {
                this.fetchPickingStats();
                this.fetchCheckingStats();
            }
        },

        picker () {
            this.fetchOrdersPickedByPicker();
            this.fetchOrderQueue();
        },
    }
}
</script>

<style scoped>
.currentPicker td {
    @apply bg-blue-100 font-bold;
}

.currentChecker td {
    @apply bg-blue-100 font-bold;
}
</style>